import moment from 'moment';

import { Translate } from 'core/i18n/types';
import {
  MessageMask,
  SendCommunicationCustomersSelection,
  SendCommunicationContent,
  SendCommunicationFirstStepForm,
  SendCommunicationFormValues,
  SendCommunicationSecondStepForm,
  SendCommunicationChannels,
  ServiceInfo,
  ServicesPassengersConcernedByCommunicationParams,
  SendCommunicationPurchaseComparisonValues,
  InitializeCommunicationResponse,
  TargetType,
  CompensationSentence,
} from 'types';

/**
 * Used in the second step to determine if the communication involves all stations.
 */
export const checkIfAllStationsConcerned = (formValuesFirstStep: SendCommunicationFirstStepForm) => {
  return (
    formValuesFirstStep.customersSelection === SendCommunicationCustomersSelection.All ||
    formValuesFirstStep.customersSelection === SendCommunicationCustomersSelection.DVNumbers
  );
};

/**
 * Used when goBack from step 1 to step 2 to persist the checkbox value that was been selected
 */
export const isCheckOthersWasSelected = (formValuesFirstStep: SendCommunicationFirstStepForm) => {
  return (
    formValuesFirstStep.selectedTypologiesIds?.length > 0 ||
    !!formValuesFirstStep.selectedConflictTypeId ||
    formValuesFirstStep.excludeAllConflictTypes ||
    !!formValuesFirstStep?.selectedPurchaseComparison
  );
};

/**
 * Used in step 2 to know the tagrgetType value that was selected
 * Used when goBack from step 1 to step 2 to persist the tagrgetType value that was selected
 */
export const calculateSelectedTargetType = (formValuesFirstStep: SendCommunicationFirstStepForm) => {
  if (formValuesFirstStep?.customersSelection === SendCommunicationCustomersSelection.DVNumbers) {
    return TargetType.DV_NUMBERS;
  }
  return TargetType.CLIENT_TYPE;
};

export const calculateSubtitle = ({
  selectedServices,
  translate,
}: {
  selectedServices: InitializeCommunicationResponse['services'] | undefined;
  translate: ReturnType<Translate>;
}) => {
  const subtitle =
    selectedServices && selectedServices.length
      ? selectedServices.length === 1
        ? translate('sendCommunication', 'serviceSummary', {
            name: selectedServices[0].serviceName,
            date: moment(selectedServices[0].circulationDate).format('DD/MM/YY'),
          })
        : translate('sendCommunication', 'trains') +
          ' ' +
          selectedServices
            .map((service) => {
              return `${service.serviceName} ${translate('common', 'of')} ${moment(service.circulationDate).format(
                'DD/MM/YY'
              )}`;
            })
            .join(', ') +
          ` (${selectedServices.length} ${translate('sendCommunication', 'trainsPerDate')})`
      : undefined;
  return subtitle;
};

export const getHasCompensation = (
  customersSelection: SendCommunicationCustomersSelection,
  selectedMessageMask?: MessageMask
) => {
  switch (customersSelection) {
    case 2:
      return selectedMessageMask?.hasCompensationZoneForUp;
    case 3:
      return selectedMessageMask?.hasCompensationZoneForDown;
    case 4:
      return selectedMessageMask?.hasCompensationZoneForUpOrDown;
    case 5:
      return selectedMessageMask?.hasCompensationZoneForSpecificBookings;
    case 1:
    default:
      return selectedMessageMask?.hasCompensationZoneForAll;
  }
};

export const getActiveCompensationByDelay = ({
  hasVolumeDelay,
  delayValue,
  hasCompensationZone,
  compensations,
}: {
  hasVolumeDelay?: boolean;
  delayValue?: string;
  hasCompensationZone?: boolean;
  compensations: CompensationSentence[];
}) => {
  if (!hasVolumeDelay || !delayValue || !hasCompensationZone || compensations.length === 0) {
    return null;
  }

  const [hours, minutes] = delayValue.split(':').map(Number);
  const delayMinutes = hours * 60 + minutes;

  const matchingCompensation = compensations.find((comp) => {
    const hasDelayMin = comp.delayMin !== null && comp.delayMin !== undefined;
    const hasDelayMax = comp.delayMax !== null && comp.delayMax !== undefined;

    if (!hasDelayMin && !hasDelayMax) {
      return false;
    }

    const minOk = !hasDelayMin || (typeof comp.delayMin === 'number' && delayMinutes >= comp.delayMin);
    const maxOk = !hasDelayMax || (typeof comp.delayMax === 'number' && delayMinutes <= comp.delayMax);

    return minOk && maxOk;
  });

  return matchingCompensation?.name ?? null;
};

export const sortImpactedStations = (
  impactedStations: string[],
  stationsConcerned: Array<{ shortCode: string; uicStation: string }>
) => {
  const shortCodesList: string[] = [];
  stationsConcerned?.forEach((station) => {
    if (impactedStations.includes(station.uicStation)) {
      shortCodesList.push(station.uicStation);
    }
  });
  return shortCodesList;
};

export const getOdsSummaryContent = (
  stations: { shortCode: string; uicStation: string }[],
  impactedStations: string[],
  excludedStations: string[]
): string[][] => {
  const content: string[][] = [];
  stations.forEach((item, index) => {
    if (impactedStations.includes(item.uicStation)) {
      const contentItem: string[] = [];
      for (let i = 0; i < index; i++) {
        if (!excludedStations.includes(stations[i].uicStation)) {
          contentItem.push(`${stations[i].shortCode} > ${item.shortCode}`);
        }
      }
      if (contentItem.length > 0) {
        content.push(contentItem);
      }
    }
  });
  return content;
};

export const getSelectPurchaseDateTimeList = (translate: ReturnType<Translate>) => [
  {
    id: SendCommunicationPurchaseComparisonValues.BEFORE,
    name: translate('sendCommunication', 'otherSelection', 'selectPurchase', 'before'),
  },
  {
    id: SendCommunicationPurchaseComparisonValues.AFTER,
    name: translate('sendCommunication', 'otherSelection', 'selectPurchase', 'after'),
  },
];

/**
 * 08/01/2024 First use case details:
 * - When targetType === TargetType.DVNumbers in the first step: Processes and validates the value DVNumbers entered by the user
 */
export const processAndValidateUnitElements = (
  text: string,
  t?: ReturnType<Translate>
): {
  uniqueElements: string[];
  hasError: boolean;
  errorMessage: string;
} => {
  const AllowedCharacters = /^[a-zA-Z0-9\s,;\r\n]+$/;
  const SeparatorRegex = /[,\s;\n]+/;
  const ElementLength = 6;

  if (!text) {
    return { uniqueElements: [], hasError: false, errorMessage: '' };
  }

  if (!AllowedCharacters.test(text)) {
    return {
      uniqueElements: [],
      hasError: true,
      errorMessage: t ? t('formErrors', 'FORBIDDEN_CHARACTERS_ERROR_MESSAGE') : 'AllowedCharacters error',
    };
  }

  const trimmedContent = text.replace(/[,\s;]+$/, '');
  const elements = trimmedContent.split(SeparatorRegex);

  const validElements = elements.filter((element) => element.trim().length === ElementLength);

  if (validElements.length !== elements.length) {
    return {
      uniqueElements: [],
      hasError: true,
      errorMessage: t ? t('formErrors', 'ELEMENT_LENGTH_ERROR_MESSAGE') : 'ElementLength error',
    };
  }

  const uniqueElements = Array.from(new Set(validElements));
  return { uniqueElements, hasError: false, errorMessage: '' };
};

export const prepareFetchStationsTemplatesPreviewParams = (
  selectedServices: Array<ServiceInfo>,
  formValuesFirstStep: SendCommunicationFirstStepForm,
  formValuesSecondStep: SendCommunicationSecondStepForm,
  stationsConcerned: Array<{ shortCode: string; uicStation: string }>,
  fullName: string,
  isBulkCommunication?: boolean
): SendCommunicationFormValues => {
  const {
    customersSelection,
    excludedStations,
    impactedStations,
    selectedTypologiesIds,
    selectedConflictTypeId,
    excludeAllConflictTypes,
    selectedCarriages,
    includePassengersWithoutSeats,
    selectedPurchaseComparison,
    purchaseReferenceDate,
    purchaseReferenceTime,
    DVNumbers,
  } = formValuesFirstStep;

  const {
    situationId,
    mailObject,
    mailHeadband,
    signature,
    reasonId,
    otherReasonComplementary,
    communicationChannels,
    communicationContent,
  } = formValuesSecondStep;

  const requestParams: SendCommunicationFormValues = {
    isBulkCommunication: !!isBulkCommunication,
    situationId,
    customersSelection,
    emailObject: mailObject,
    emailHeader: mailHeadband,
    sender: fullName,
    author: signature,
    excludedStations,
    impactedStations,
    selectedPassengerTypologiesIds: selectedTypologiesIds,
    conflictTypeId: selectedConflictTypeId,
    excludeAllConflictTypes,
    selectedCarriages,
    includePassengersWithoutSeats,
    reasonId,
    otherReason: otherReasonComplementary || undefined,
    communicationChannels,
    communicationContent: communicationContent?.map((content) => {
      const contentStation = stationsConcerned?.find((station) => station.uicStation === content?.uicStation);
      const newContent: SendCommunicationContent = {
        uicStation: contentStation?.uicStation,
        stationShortCode: contentStation?.shortCode,
      };
      if (content?.delay) {
        newContent.delay = content.delay;
      }
      if (content?.principalZoneLongMessage) {
        newContent.area1Long = content.principalZoneLongMessage;
      }
      if (content?.principalZoneShortMessage && communicationChannels?.includes(SendCommunicationChannels.Sms)) {
        /*
         * used for sms channel principalZone
         * The trim is to prevent unecessary spaces and then decrease the sms credits (aka price ^^)
         */
        newContent.area1Short = content.principalZoneShortMessage.trim();
      }
      if (content?.secondaryZoneLongMessage) {
        newContent.area2Long = content.secondaryZoneLongMessage;
      }
      if (content?.secondaryZoneShortMessage && communicationChannels?.includes(SendCommunicationChannels.Sms)) {
        /*
         * used for sms channel secondaryZone
         * The trim is to prevent unecessary spaces and then decrease the sms credits (aka price ^^)
         */
        newContent.area2Short = content.secondaryZoneShortMessage.trim();
      }
      if (content?.dock) {
        newContent.dock = content.dock;
      }
      if (content?.newSchedule) {
        newContent.newSchedule = content.newSchedule;
      }
      if (content?.stationReplacement) {
        newContent.stationReplacement = content.stationReplacement;
      }
      if (content?.compensationLongMessage) {
        newContent.compensationLongMessage = content.compensationLongMessage;
      }
      if (content?.compensationId) {
        newContent.compensationId = content.compensationId;
      }
      if (content?.compensationSelected) {
        newContent.compensationSelected = content.compensationSelected;
      }
      return newContent;
    }),
  };
  if (selectedServices) {
    requestParams.serviceName = selectedServices[0]?.serviceName;
    requestParams.serviceDate = selectedServices[0]?.serviceDate;
  }
  if (!!selectedPurchaseComparison && !!purchaseReferenceDate && !!purchaseReferenceTime) {
    const selectedPurchaseReferenceDateTime = `${purchaseReferenceDate}T${purchaseReferenceTime}:00`;
    requestParams.selectedPurchase = {
      purchaseDateTimeComparison: selectedPurchaseComparison,
      purchaseReferenceDateTime: selectedPurchaseReferenceDateTime,
    };
  }
  if (customersSelection === SendCommunicationCustomersSelection.DVNumbers && DVNumbers) {
    const { uniqueElements } = processAndValidateUnitElements(DVNumbers);
    if (uniqueElements.length) {
      requestParams.specificBookings = uniqueElements;
    }
  }
  return requestParams;
};

export const prepareSaveCommunicationsParams = ({
  selectedServices,
  formValuesFirstStep,
  formValuesSecondStep,
  stationsConcerned,
  fullName,
  isBulkCommunication,
  maxDaysBeforeDeferredSending,
  overviewOD,
}: {
  selectedServices: Array<ServiceInfo>;
  formValuesFirstStep: SendCommunicationFirstStepForm;
  formValuesSecondStep: SendCommunicationSecondStepForm;
  stationsConcerned: Array<{ shortCode: string; uicStation: string }>;
  fullName: string;
  isBulkCommunication: boolean;
  maxDaysBeforeDeferredSending?: number;
  overviewOD: string[];
}): SendCommunicationFormValues => {
  const {
    customersSelection,
    excludedStations,
    impactedStations,
    selectedTypologiesIds,
    selectedConflictTypeId,
    excludeAllConflictTypes,
    selectedCarriages,
    includePassengersWithoutSeats,
    selectedPurchaseComparison,
    purchaseReferenceDate,
    purchaseReferenceTime,
    DVNumbers,
  } = formValuesFirstStep;

  const {
    situationId,
    mailObject,
    mailHeadband,
    signature,
    reasonId,
    otherReasonComplementary,
    communicationChannels,
    communicationContent,
    isDifferedCommunication,
    differedDaysBeforeCirculation,
    differedCommunicationTime,
  } = formValuesSecondStep;

  const requestParams: SendCommunicationFormValues = {
    isBulkCommunication: isBulkCommunication,
    maxDaysBeforeDeferredSending: maxDaysBeforeDeferredSending,
    isDifferedCommunication,
    situationId,
    customersSelection,
    emailObject: mailObject,
    emailHeader: mailHeadband,
    sender: fullName,
    author: signature,
    excludedStations,
    impactedStations,
    selectedPassengerTypologiesIds: selectedTypologiesIds,
    conflictTypeId: selectedConflictTypeId,
    excludeAllConflictTypes,
    selectedCarriages,
    includePassengersWithoutSeats,
    reasonId,
    otherReason: otherReasonComplementary || undefined,
    communicationChannels,
    communicationContent: communicationContent?.map((content) => {
      const contentStation = stationsConcerned?.find((station) => station.uicStation === content?.uicStation);
      const newContent: SendCommunicationContent = {
        uicStation: contentStation?.uicStation,
        stationShortCode: contentStation?.shortCode,
      };
      if (content?.delay) {
        newContent.delay = content.delay;
      }
      if (content?.principalZoneShortMessage && communicationChannels?.includes(SendCommunicationChannels.Sms)) {
        /*
         * used for sms channel principalZone
         * The trim is to prevent unecessary spaces and then decrease the sms credits (aka price ^^)
         */
        newContent.area1Short = content.principalZoneShortMessage.trim();
      }
      if (content?.principalZoneLongMessage) {
        newContent.area1Long = content.principalZoneLongMessage;
      }
      if (content?.secondaryZoneShortMessage && communicationChannels?.includes(SendCommunicationChannels.Sms)) {
        /*
         * used for sms channel secondaryZone
         * The trim is to prevent unecessary spaces and then decrease the sms credits (aka price ^^)
         */
        newContent.area2Short = content.secondaryZoneShortMessage.trim();
      }
      if (content?.secondaryZoneLongMessage) {
        newContent.area2Long = content.secondaryZoneLongMessage;
      }
      if (content?.dock) {
        newContent.dock = content.dock;
      }
      if (content?.newSchedule) {
        newContent.newSchedule = content.newSchedule;
      }
      if (content?.stationReplacement) {
        newContent.stationReplacement = content.stationReplacement;
      }
      if (content?.compensationLongMessage) {
        newContent.compensationLongMessage = content.compensationLongMessage;
      }
      if (content?.compensationId) {
        newContent.compensationId = content.compensationId;
      }
      if (content?.compensationSelected) {
        newContent.compensationSelected = content.compensationSelected;
      }
      return newContent;
    }),
  };
  if (selectedServices?.length) {
    requestParams.selectedServices = selectedServices;
  }
  if (isDifferedCommunication) {
    requestParams.differedCommunicationProperties = {
      daysBeforeCirculation: differedDaysBeforeCirculation,
      communicationTime: differedCommunicationTime,
    };
  }
  if (!!selectedPurchaseComparison && !!purchaseReferenceDate && !!purchaseReferenceTime) {
    const selectedPurchaseReferenceDateTime = `${purchaseReferenceDate}T${purchaseReferenceTime}:00`;
    requestParams.selectedPurchase = {
      purchaseDateTimeComparison: selectedPurchaseComparison,
      purchaseReferenceDateTime: selectedPurchaseReferenceDateTime,
    };
  }
  if (customersSelection === SendCommunicationCustomersSelection.DVNumbers && DVNumbers) {
    const { uniqueElements } = processAndValidateUnitElements(DVNumbers);
    if (uniqueElements.length) {
      requestParams.specificBookings = uniqueElements;
    }
  }

  if (overviewOD.length > 0) {
    requestParams.overviewOD = overviewOD;
  }

  return requestParams;
};

export const prepareServicesPassengersConcernedByCommunicationParams = (
  selectedServices: Array<ServiceInfo> = [],
  formValuesFirstStep: SendCommunicationFirstStepForm
): ServicesPassengersConcernedByCommunicationParams => {
  const {
    selectedCarriages,
    impactedStations,
    excludedStations,
    selectedTypologiesIds,
    customersSelection,
    includePassengersWithoutSeats,
    selectedConflictTypeId,
    excludeAllConflictTypes,
    selectedPurchaseComparison,
    purchaseReferenceDate,
    purchaseReferenceTime,
    DVNumbers,
  } = formValuesFirstStep;

  const requestParams: ServicesPassengersConcernedByCommunicationParams = {
    selectedServices,
    selectedClients: customersSelection,
    includePassengersWithoutSeats,
    excludeAllConflictTypes,
  };
  if (selectedCarriages?.length) {
    requestParams.selectedCarriages = selectedCarriages;
  }
  if (impactedStations?.length) {
    requestParams.impactedStations = impactedStations;
  }
  if (excludedStations?.length) {
    requestParams.excludedStations = excludedStations;
  }
  if (selectedTypologiesIds?.length) {
    requestParams.selectedPassengerTypologiesIds = selectedTypologiesIds;
  }
  if (selectedConflictTypeId) {
    const selectedConflictTypeIds = [];
    selectedConflictTypeIds.push(selectedConflictTypeId);
    requestParams.conflictTypes = selectedConflictTypeIds;
  }
  if (!!selectedPurchaseComparison && !!purchaseReferenceDate && !!purchaseReferenceTime) {
    const selectedPurchaseReferenceDateTime = `${purchaseReferenceDate}T${purchaseReferenceTime}:00`;
    requestParams.selectedPurchase = {
      purchaseDateTimeComparison: selectedPurchaseComparison,
      purchaseReferenceDateTime: selectedPurchaseReferenceDateTime,
    };
  }
  if (customersSelection === SendCommunicationCustomersSelection.DVNumbers && DVNumbers) {
    const { uniqueElements } = processAndValidateUnitElements(DVNumbers);
    if (uniqueElements.length) {
      requestParams.specificBookings = uniqueElements;
    }
  }

  return requestParams;
};
